import React, { useMemo, useState } from "react";
import {
    AccountingReportType,
    ACCOUNTING_REPORT_LABELS,
} from "@common/types/reports/report-type";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { useTaxPackageDownload } from "@components/accounting/useTaxPackageDownload";
import { Card } from "@design-system/Card/Card";
import { usePinnedDatePresets } from "@hooks/usePinnedDatePresets";
import { ORGANIZATION_USER_CACHE_KEYS } from "@hooks/useOrganizationUserCache";
import { SUGGESTED_PRESETS } from "@helpers/date";
import { CHECK_DETAIL_REPORT_DESCRIPTION } from "@components/reporting/reportingCatalog";
import { AccountingReportFiltersForm } from "./AccountingReportFiltersForm";
import {
    AccountingReportItem,
    useConsolidatedAccountingReports,
} from "./useConsolidatedAccountingReports";
import {
    getDefaultEndDate,
    getDefaultStartDate,
    useProfitLossInitialDates,
} from "./useProfitLossInitialDates";
import { useAccountingTabContext } from "./useAccountingTab";
import { useEntitiesWithAccountingAvailable } from "./useEntitiesWithAccountingAvailable";
import { AccountingReportLink } from "./AccountingReportLink";
import { SCHEDULES_REPORT_NAV_ENTRIES } from "./schedulesReport/schedulesReportCatalogEntries";
import { buildSchedulesReportUrl } from "./schedulesReport/schedulesReportDeepLink";
import {
    FORM_1099_NEC_REPORT_DESCRIPTION,
    FORM_1099_NEC_REPORT_LABEL,
} from "./form1099Nec/form1099NecFormat";
import { ReportPackageBuilderDrawer } from "./ReportPackageBuilderDrawer/ReportPackageBuilderDrawer";
import { ReportPackageModal } from "./ReportPackageModal/ReportPackageModal";
import styles from "./AccountingPage.module.scss";

export const AccountingPage: React.FC = () => {
    const { filters, setFilters, selectedEntity } = useAccountingTabContext();

    const availableEntities = useEntitiesWithAccountingAvailable();
    const entityId = filters.entityId ?? availableEntities[0]?.id;

    const { dateStart, dateEnd } = useProfitLossInitialDates();
    const filtersWithDefaultDatesAsFallback = useMemo(
        () => ({
            ...filters,
            startDate: filters.startDate ?? getDefaultStartDate(),
            endDate: filters.endDate ?? getDefaultEndDate(),
        }),
        [filters],
    );

    const {
        schedules: canUseSchedules,
        form1099NecReport: canUseForm1099NecReport,
        expandedReportPackage: isExpandedReportPackageEnabled,
    } = useTypedFlags();
    const ReportPackageDialog = isExpandedReportPackageEnabled
        ? ReportPackageBuilderDrawer
        : ReportPackageModal;

    const {
        download: downloadTaxPackage,
        isDownloading: isDownloadingTaxPackage,
    } = useTaxPackageDownload(selectedEntity, filters.ledgerId);

    const [showReportPackageModal, setShowReportPackageModal] = useState(false);

    const { pinnedPresets, onTogglePin } = usePinnedDatePresets({
        cacheKey:
            ORGANIZATION_USER_CACHE_KEYS.PINNED_DATE_PRESETS_ACCOUNTING_PAGE_CACHE_KEY,
        defaults: SUGGESTED_PRESETS.accounting,
    });

    const defaultDateRange = useMemo(
        () => ({ start: dateStart, end: dateEnd }),
        [dateStart, dateEnd],
    );
    const reportQuerySuffix = `startDate=${dateStart}&endDate=${dateEnd}&entityId=${entityId}${
        filters.ledgerId ? `&ledgerId=${filters.ledgerId}` : ""
    }`;

    const consolidatedReports = useConsolidatedAccountingReports({
        consolidationGroupId: filters.consolidationGroupId,
        startDate: dateStart,
        endDate: dateEnd,
    });

    // Define accounting reports data structure
    const entityReports = useMemo(
        (): AccountingReportItem[] => [
            {
                id: "profit-loss",
                title: ACCOUNTING_REPORT_LABELS[
                    AccountingReportType.PROFIT_LOSS
                ],
                description:
                    "Reports your income and expenses over a specified period of time",
                link: `/accounting/profit-loss?${reportQuerySuffix}`,
                active: true,
            },
            {
                id: "balance-sheet",
                title: (
                    <div className={styles.accountingLinkContainer}>
                        <span>
                            {
                                ACCOUNTING_REPORT_LABELS[
                                    AccountingReportType.BALANCE_SHEET
                                ]
                            }
                        </span>
                    </div>
                ),
                description:
                    "Snapshot of your financial position at a given point of time, broken down into assets, liabilities, and equity",
                link: `/accounting/balance-sheet?${reportQuerySuffix}`,
                active: true,
            },
            {
                id: "general-ledger",
                title: ACCOUNTING_REPORT_LABELS[
                    AccountingReportType.GENERAL_LEDGER
                ],
                description:
                    "Record of all transactions in your business that helps in preparing financial statements",
                link: `/accounting/general-ledger?${reportQuerySuffix}`,
                active: true,
            },
            {
                id: "trial-balance",
                title: (
                    <div className={styles.accountingLinkContainer}>
                        <span>
                            {
                                ACCOUNTING_REPORT_LABELS[
                                    AccountingReportType.TRIAL_BALANCE
                                ]
                            }
                        </span>
                    </div>
                ),
                description:
                    "Report that checks the accuracy of your books by listing all account balances, ensuring total debits match total credits",
                link: `/accounting/trial-balance?${reportQuerySuffix}`,
                active: true,
            },
            {
                id: "expenses-by-vendor",
                title: ACCOUNTING_REPORT_LABELS[
                    AccountingReportType.EXPENSE_BY_VENDOR
                ],
                description:
                    "Summary of expenses grouped by vendor over a period",
                link: `/accounting/expenses-by-vendor?${reportQuerySuffix}`,
                active: true,
            },
            {
                id: "cash-flow-statement",
                title: ACCOUNTING_REPORT_LABELS[
                    AccountingReportType.CASH_FLOW_STATEMENT
                ],
                description:
                    "Summary of cash inflows and outflows using the indirect method, showing operating, investing, and financing activities",
                link: `/accounting/cash-flow-statement?${reportQuerySuffix}`,
                active: true,
            },
            {
                id: "ar-aging-summary",
                title: ACCOUNTING_REPORT_LABELS[
                    AccountingReportType.AR_AGING_SUMMARY
                ],
                description:
                    "Open invoices grouped by customer and aging bucket as of a given date",
                link: "/accounting/ar-aging-summary",
                active: true,
            },
            {
                id: "ar-aging-detail",
                title: ACCOUNTING_REPORT_LABELS[
                    AccountingReportType.AR_AGING_DETAIL
                ],
                description:
                    "Open invoices that make up each aging bucket as of a given date",
                link: "/accounting/ar-aging-detail",
                active: true,
            },
            ...(canUseSchedules
                ? SCHEDULES_REPORT_NAV_ENTRIES.map((entry) => ({
                      id: entry.id,
                      title: entry.title,
                      description: entry.description,
                      link: buildSchedulesReportUrl(
                          reportQuerySuffix,
                          entry.viewId,
                      ),
                      active: true,
                  }))
                : []),
            {
                id: "check-detail",
                title: ACCOUNTING_REPORT_LABELS[
                    AccountingReportType.CHECK_DETAIL
                ],
                description: CHECK_DETAIL_REPORT_DESCRIPTION,
                link: `/accounting/check-detail?${reportQuerySuffix}`,
                active: true,
            },
            ...(canUseForm1099NecReport
                ? [
                      {
                          id: "1099-nec",
                          title: FORM_1099_NEC_REPORT_LABEL,
                          description: FORM_1099_NEC_REPORT_DESCRIPTION,
                          link: "/accounting/1099-nec",
                          active: true,
                      },
                  ]
                : []),
            {
                id: "tax-package",
                title: (
                    <div className={styles.accountingLinkContainer}>
                        <span>2025 Year-End Tax Package</span>
                    </div>
                ),
                description:
                    "Tax Package includes a full-year General Ledger, Trial Balance, Profit and Loss Statement, and a Balance Sheet",
                onClick: downloadTaxPackage,
                loading: isDownloadingTaxPackage,
            },
            {
                id: "report-package",
                title: "Custom Report Package",
                description:
                    "Create a custom package with selected reports for your entities",
                active: true,
                onClick: () => setShowReportPackageModal(true),
            },
        ],
        [
            reportQuerySuffix,
            canUseSchedules,
            canUseForm1099NecReport,
            isDownloadingTaxPackage,
            downloadTaxPackage,
        ],
    );

    const accountingReports = consolidatedReports ?? entityReports;

    // Filter reports based on search term
    const filteredReports = useMemo(() => {
        if (!filtersWithDefaultDatesAsFallback.search) {
            return accountingReports;
        }

        const searchTerm =
            filtersWithDefaultDatesAsFallback.search.toLowerCase();
        return accountingReports.filter((report) => {
            const titleText =
                typeof report.title === "string"
                    ? report.title.toLowerCase()
                    : ""; // Handle React elements
            return (
                report.description.toLowerCase().includes(searchTerm) ||
                titleText.includes(searchTerm) ||
                report.id.toLowerCase().includes(searchTerm)
            );
        });
    }, [accountingReports, filtersWithDefaultDatesAsFallback.search]);

    return (
        <div className="flex flex-col w-100">
            <AccountingReportFiltersForm
                filters={filtersWithDefaultDatesAsFallback}
                setFilters={setFilters}
                pinnedPresets={pinnedPresets}
                onTogglePin={onTogglePin}
                requireDateSelection
                hideClassFilter
                hideEntityFilter={Boolean(consolidatedReports)}
            />
            <Card
                label="Accountant reports"
                sharpEdges
                fullPage
                labelClassName={styles.reportsLabel}
            >
                {filteredReports.map((report) => (
                    <AccountingReportLink
                        key={report.id}
                        title={report.title}
                        description={report.description}
                        link={report.link}
                        active={report.active}
                        onClick={report.onClick}
                        loading={report.loading}
                    />
                ))}
            </Card>
            {filteredReports.length === 0 && (
                <EmptyState header="No reports found for the provided search" />
            )}
            <ReportPackageDialog
                show={showReportPackageModal}
                onHide={() => setShowReportPackageModal(false)}
                defaultEntityId={entityId}
                defaultDateRange={defaultDateRange}
                ledgerId={filters.ledgerId}
            />
        </div>
    );
};
